// §manufacturer-pharmacy — one-click routing of a branded GLP-1 prescription to
// the manufacturer's own direct-to-patient pharmacy.
//
// Both records below are REAL entries in Beluga's pharmacy directory, resolved
// via POST /beluga/pharmacy-search on 2026-08-21. The `pharmacyId` values are
// Beluga PharmacyIds — the same identifiers PharmacySelectStep returns — so a
// selection made here travels the existing `pharmacySelection: { mode: 'own' }`
// envelope with no backend change. Do NOT hand-edit these ids; re-run the
// search if a record needs updating.
//
//   LillyDirect Pharmacy  → PharmacyId 1957780, NCPDP 1574056
//   NovoCare Pharmacy     → PharmacyId 1955519, NCPDP 5758074 (NPI 1710671854)
//
// NovoCare's NCPDP/NPI/address match novocare.com/hcp/pharmacy.html exactly.
// LillyDirect's address matches lilly.com's published pharmacy card.
//
// Note there are TWO "LillyDirect" records in Beluga's directory: 1957780
// (LillyDirect Pharmacy, Indianapolis IN) and 272448 ("LillyDirect Self Pay
// Pharmacy Solut", Columbus OH). We use the Indianapolis one — it is the
// address Lilly publishes as LillyDirect Pharmacy.

export const MANUFACTURER_PHARMACIES = {
  lilly: {
    key: 'lilly',
    brand: 'LillyDirect',
    // Shape matches PharmacySelectStep's onAnswer payload so it drops straight
    // into `localPharmacy` and the pharmacySelection envelope.
    pharmacy: {
      pharmacyId: '1957780',
      storeName: 'LillyDirect Pharmacy',
      address1: '1555 S Harding St, Suite 171-B68',
      city: 'Indianapolis',
      state: 'IN',
      zip: '46221',
    },
    headline: 'Send this prescription to LillyDirect Pharmacy',
    // Deliberately factual and non-promotional about price: manufacturer self-pay
    // pricing changes and is not ours to quote. Say what the option IS.
    body:
      "This medication is made by Eli Lilly. LillyDirect is Lilly's own pharmacy — your provider can send the prescription straight there, and it ships to your home.",
    learnMoreUrl: 'https://www.lilly.com/lillydirect',
  },
  novo: {
    key: 'novo',
    brand: 'NovoCare',
    pharmacy: {
      pharmacyId: '1955519',
      storeName: 'NovoCare Pharmacy',
      address1: '2400 Sand Lake Road, Suite 200B',
      city: 'Orlando',
      state: 'FL',
      zip: '32809',
    },
    headline: 'Send this prescription to NovoCare® Pharmacy',
    body:
      'This medication is made by Novo Nordisk. NovoCare® Pharmacy is their direct-to-patient pharmacy — your provider can send the prescription straight there, and it ships to your home at no delivery cost.',
    learnMoreUrl: 'https://www.novocare.com/hcp/pharmacy.html',
  },
};

// Which manufacturer, if any, dispenses a given catalog SKU. Matched on the
// product name because that is the only manufacturer signal the catalog carries
// (there is no `manufacturer` field on /catalog/medications today).
//
//   Lilly — Zepbound & Mounjaro (tirzepatide), Foundayo (orforglipron)
//   Novo  — Wegovy & Ozempic (semaglutide), pill and pen alike
//
// Anything else returns null and the checkout is unchanged.
const LILLY_RE = /zepbound|mounjaro|foundayo|orforglipron/i;
const NOVO_RE = /wegovy|ozempic/i;

export const resolveManufacturerPharmacy = (item) => {
  const name = (item && (item.name || item.medication)) || '';
  if (!name) return null;
  if (LILLY_RE.test(name)) return MANUFACTURER_PHARMACIES.lilly;
  if (NOVO_RE.test(name)) return MANUFACTURER_PHARMACIES.novo;
  return null;
};

// True when `selected` is already the manufacturer's pharmacy, so the banner can
// render as chosen rather than offering a redundant action.
export const isManufacturerPharmacySelected = (selected, mfr) =>
  Boolean(selected && mfr && String(selected.pharmacyId) === String(mfr.pharmacy.pharmacyId));

// §destination-badge — one line naming where the prescription is actually going,
// rendered under the fulfillment fork so the patient can confirm the destination
// without scrolling back through three cards to reconstruct it.
//
// The four states are distinct on purpose. 'pending' is the one that matters:
// choosing "my own pharmacy" and then not picking a store is the state that
// silently blocks payment, and before this badge the only hint was a small amber
// line under the search box.
export const PHARMACY_DESTINATION = {
  PARTNER: 'partner',
  MANUFACTURER: 'manufacturer',
  OWN: 'own',
  PENDING: 'pending',
};

/**
 * Describe where the prescription is headed, for the badge.
 * @param {boolean} isLocal      true when the patient chose their own pharmacy
 * @param {object}  localPharmacy the selected pharmacy record, if any
 * @param {object}  mfr          the manufacturer pharmacy for this SKU, if any
 * @returns {{kind: string, label: string, detail: string}}
 */
export function describePharmacyDestination({ isLocal, localPharmacy, mfr } = {}) {
  if (!isLocal) {
    return {
      kind: PHARMACY_DESTINATION.PARTNER,
      label: 'Partner pharmacy',
      detail: 'Ships discreetly to your door',
    };
  }
  if (!localPharmacy || !localPharmacy.pharmacyId) {
    return {
      kind: PHARMACY_DESTINATION.PENDING,
      label: 'No pharmacy chosen yet',
      detail: 'Pick one below to continue',
    };
  }
  if (isManufacturerPharmacySelected(localPharmacy, mfr)) {
    return {
      kind: PHARMACY_DESTINATION.MANUFACTURER,
      label: mfr.pharmacy.storeName,
      // Say that it SHIPS. This badge sits under a fork option whose generic
      // label is "I will pick it up locally", and the manufacturer path is the
      // one case where that is false — the manufacturer is mail-order.
      detail: `Ships to your home from ${mfr.brand} — nothing to pick up`,
    };
  }
  return {
    kind: PHARMACY_DESTINATION.OWN,
    label: localPharmacy.storeName || 'Your pharmacy',
    detail: [localPharmacy.city, localPharmacy.state].filter(Boolean).join(', ') || 'Pick up at your pharmacy',
  };
}
